const INDENT_UNIT = '  ';
const RECENT_MEMORY_LIMIT = 20;

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function isMeaningfulValue(value) {
  if (value == null) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  if (Array.isArray(value)) {
    return value.some(item => isMeaningfulValue(item));
  }

  if (isPlainObject(value)) {
    return Object.values(value).some(item => isMeaningfulValue(item));
  }

  return true;
}

function normalizePromptValue(value) {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    return value
      .map(item => normalizePromptValue(item))
      .filter(item => isMeaningfulValue(item));
  }

  if (isPlainObject(value)) {
    return Object.entries(value).reduce((result, [key, item]) => {
      const normalizedItem = normalizePromptValue(item);
      if (isMeaningfulValue(normalizedItem)) {
        result[key] = normalizedItem;
      }
      return result;
    }, {});
  }

  return value;
}

function normalizeSoulValue(soul) {
  const normalizedSoul = normalizePromptValue(soul);
  if (!isPlainObject(normalizedSoul)) {
    return normalizedSoul;
  }

  const keys = Object.keys(normalizedSoul);
  if (keys.length === 1 && typeof normalizedSoul.personality === 'string') {
    return normalizedSoul.personality;
  }

  return normalizedSoul;
}

function normalizeMemoryValue(memory) {
  if (Array.isArray(memory)) {
    return normalizePromptValue(memory.slice(-RECENT_MEMORY_LIMIT));
  }

  const normalizedMemory = normalizePromptValue(memory);
  if (!isPlainObject(normalizedMemory)) {
    return normalizedMemory;
  }

  if (Array.isArray(normalizedMemory.longTerm)) {
    const recentLongTerm = normalizedMemory.longTerm.slice(-RECENT_MEMORY_LIMIT);
    const keys = Object.keys(normalizedMemory);

    if (keys.length === 1) {
      return recentLongTerm;
    }

    return {
      ...normalizedMemory,
      longTerm: recentLongTerm
    };
  }

  return normalizedMemory;
}

function formatScalarLines(value, indentLevel = 0) {
  const indent = INDENT_UNIT.repeat(indentLevel);
  const lines = String(value)
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return [`${indent}- None`];
  }

  if (lines.length === 1) {
    return [`${indent}- ${lines[0]}`];
  }

  return [
    `${indent}-`,
    ...lines.map(line => `${INDENT_UNIT.repeat(indentLevel + 1)}${line}`)
  ];
}

function formatMarkdownLines(value, indentLevel = 0) {
  const indent = INDENT_UNIT.repeat(indentLevel);

  if (!isMeaningfulValue(value)) {
    return [`${indent}- None`];
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return formatScalarLines(value, indentLevel);
  }

  if (Array.isArray(value)) {
    return value.flatMap((item, index) => {
      if (!isMeaningfulValue(item)) {
        return [];
      }

      if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
        return formatScalarLines(item, indentLevel);
      }

      return [
        `${indent}- Item ${index + 1}:`,
        ...formatMarkdownLines(item, indentLevel + 1)
      ];
    });
  }

  if (isPlainObject(value)) {
    return Object.entries(value).flatMap(([key, item]) => {
      if (!isMeaningfulValue(item)) {
        return [];
      }

      if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
        const scalarLines = String(item)
          .split('\n')
          .map(line => line.trim())
          .filter(Boolean);

        if (scalarLines.length <= 1) {
          return [`${indent}- ${key}: ${scalarLines[0] || 'None'}`];
        }

        return [
          `${indent}- ${key}:`,
          ...scalarLines.map(line => `${INDENT_UNIT.repeat(indentLevel + 1)}- ${line}`)
        ];
      }

      return [
        `${indent}- ${key}:`,
        ...formatMarkdownLines(item, indentLevel + 1)
      ];
    });
  }

  return [`${indent}- ${String(value)}`];
}

function formatSection(title, value) {
  if (!isMeaningfulValue(value)) {
    return `### ${title}\n- None`;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    const text = String(value).trim();
    return `### ${title}\n${text || 'None'}`;
  }

  return `### ${title}\n${formatMarkdownLines(value).join('\n')}`;
}

export function buildCoreMemoryMarkdown({ soul, user, memory }) {
  return [
    '## Core Memory System',
    '',
    formatSection('Soul', normalizeSoulValue(soul)),
    '',
    formatSection('User Profile', normalizePromptValue(user)),
    '',
    formatSection('Long-term Memory', normalizeMemoryValue(memory))
  ].join('\n').trim();
}
